/**
 * Finds the index of the next word in a text starting from a given index.
 * @param text - The text to search.
 * @param startIndex - The index to start searching from.
 * @returns the start index of the next word or the end of the text.
 * @internal
 */
export const findNextWordIndex = (text: string, startIndex: number): number => {
  if (startIndex >= text.length) {
    // early exit if startIndex is already at the end of the text
    return text.length;
  }
  const remainingText = text.slice(startIndex);

  // if the current character is a whitespace, find the next word
  const isWhitespace = /\s/.test(text[startIndex]);
  if (isWhitespace) {
    const nextWordIndex = remainingText.search(/\S/);
    return nextWordIndex === -1 ? text.length : startIndex + nextWordIndex;
  }

  // if the current character is a word, find the start of the next word
  // by first finding the next whitespace and then the next non-whitespace character
  const nextWhitespaceIndex = remainingText.search(/\s/);
  if (nextWhitespaceIndex === -1) {
    // early exit if we are already at the last word
    return text.length;
  }

  const nextWordIndex = remainingText.slice(nextWhitespaceIndex).search(/\S/);
  return nextWordIndex === -1
    ? text.length
    : startIndex + nextWhitespaceIndex + nextWordIndex;
};

/**
 * Finds the index where two strings first differ.
 * @param oldText - The already rendered string.
 * @param newText - The new string to be rendered.
 * @returns the index where strings first differ.
 * @internal
 */
export const findFirstDiffIndex = (
  oldText: string,
  newText: string,
): number => {
  const maxIndex = Math.min(oldText.length, newText.length);
  for (let i = 0; i < maxIndex; i++) {
    if (oldText[i] !== newText[i]) {
      return i;
    }
  }
  return maxIndex;
};
